// @givmo/app-admin — "Needs attention" (dashboard block 1; EP-DES1 §3).
//
// The highest-value thing on the admin home and the reason the approvals split was worth its e2e cost:
// the dashboard now leads with what a human has to DO, and `/approvals` owns the work surface.
//
// DATA-DRIVEN, HIDDEN WHEN EMPTY. With nothing pending the block renders `null` — not an empty card,
// not a "you're all caught up" panel. A block that always renders is chrome; a block that only appears
// when it has something to say is a signal, and the whole design bet here is that the admin learns to
// trust it.
//
// Its testids are a NEW `needs-attention-*` namespace, deliberately disjoint from the queue's
// `approval-*` vocabulary. Two reasons: nothing double-counts when both surfaces are up, and no e2e
// selector accidentally pins this block's data-driven visibility (a spec that asserted the summary
// exists would fail the moment the queue emptied — which is the block working correctly).
//
// SIGNALS (each independently data-driven; the block appears if ANY has something):
//   1. Pending approvals — count + the dollars Approve all would match + the top rows (each at what
//      approving it matches, never the employee's gift: D-EP-56) with their real Approve/Decline
//      actions + Approve-all + "See all". The actions are the SAME `useMatchApprovals` callbacks the
//      queue uses, so a decision taken here is byte-identically the decision taken there.
//   2. Pending join requests — count only, linking to Admin Team which owns the inbox. Double-gated
//      (flag + Owner); an unobservable read renders nothing rather than a zero.
//   3. Low wallet balance — a UI-CONSTANT threshold (see the note on it below).
//   4. AI requests waiting for THIS person (MCP-P6a) — count only, linking to the host's inbox. The
//      host supplies the door and its count (`paths.aiRequests`); the portal has none, and an unknown
//      or unserved count (a dark environment's 404) draws nothing rather than a zero.
//
// NOT HERE YET: failed gift-card deliveries. The per-status `delivery_counts.failed` is DETAIL-only on
// the wire (the batch LIST item carries only enqueue counters), so the honest version of that card
// needs an additive `delivery_counts` on the list item. The seam is this file's signal list — add a
// fourth entry when the field lands. No placeholder ships in the meantime.

import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { companiesGetCompanyWalletOptions } from '@givmo/api-client/query';
import { Badge, Button, Icon, testIdProp } from '@givmo/design-system';
import {
  AlertCircle,
  ArrowRight,
  BotMessageSquare,
  Check,
  UserPlus,
  Wallet,
  X,
} from 'lucide-react';
import { formatMoneyWithSymbol } from '../../settings/wallet-decode';
import { isCompanyJoinOrCreateEnabled } from '../../../lib/env';
import { useEmployerModulePaths } from '../../../lib/module-paths';
import { usePendingJoinRequests } from '../../join-requests/use-pending-join-requests';
import {
  charityCountLabel,
  formatApprovalUsd,
  groupRowTitle,
  parseCentsAmount,
  type MatchApprovalRow,
} from '../dashboard-decode';

/** How many pending rows the summary shows before deferring to the queue. */
export const NEEDS_ATTENTION_ROW_LIMIT = 3;

/**
 * The low-balance threshold, in dollars. This is a UI CONSTANT — the backend has no low-balance
 * concept at all (no threshold column, no alert, nothing on the wire), so the copy must never imply
 * the company chose this number. It says "we flag", because we do.
 */
export const LOW_WALLET_BALANCE_DOLLARS = 100;

/**
 * Whether to flag the wallet, from its spendable balance in exact integer cents.
 *
 * The rule is `0 < available < threshold` — a wallet that is RUNNING LOW. An exactly-zero wallet is
 * deliberately NOT flagged, because the wire cannot distinguish "drained" from "this company funds
 * everything by card and has never used the wallet", and nagging the second company forever on its
 * home screen would make the whole block untrustworthy. The drained case is not left uncovered: the
 * approve dialog's own insufficient-funds path is the authoritative, in-context, role-aware signal at
 * the moment it actually matters. A wire signal for "wallet is in use" (or a real server-side
 * threshold) is what would let this flag zero honestly.
 */
export function isWalletLow(availableCents: number | null): boolean {
  if (availableCents === null) return false;
  return availableCents > 0 && availableCents < LOW_WALLET_BALANCE_DOLLARS * 100;
}

/** A host with no AI requests inbox: nothing waits. */
function useNoAiRequestsWaiting(): null {
  return null;
}

/**
 * The AI requests line's headline (DRAFT, design-8's question 12): "2 AI requests are waiting for
 * you", with a `+` when the count may not be the total.
 */
export function aiRequestsWaitingTitle(count: number, atLeast: boolean): string {
  const one = count === 1 && !atLeast;
  return `${count}${atLeast ? '+' : ''} ${one ? 'AI request is' : 'AI requests are'} waiting for you`;
}

/** One row of the summary: who asked for what, and the two decisions available on it. */
function SummaryRow({
  row,
  canManage,
  rejectingId,
  onApprove,
  onDecline,
}: {
  row: MatchApprovalRow;
  canManage: boolean;
  rejectingId: string | null;
  onApprove: (row: MatchApprovalRow) => void;
  onDecline: (row: MatchApprovalRow) => void;
}) {
  const target = row.kind === 'grouped' ? groupRowTitle(row) : row.charity.name;
  return (
    <li
      className="flex flex-wrap items-center justify-between gap-12 border-t border-border-subtle py-10 first:border-t-0 first:pt-0"
      {...testIdProp(`needs-attention-approval-row-${row.id}`)}
    >
      <div className="flex min-w-0 flex-col gap-2">
        <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          {row.employeeName} <span className="font-w400 text-text-tertiary">→</span> {target}
        </span>
        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          {/* What approving it matches (D-EP-56: never the employee's gift), its budget note, and
              for a grouped request its charity count. */}
          {[
            row.amount,
            row.amountNote,
            row.kind === 'grouped' ? charityCountLabel(row.members.length) : null,
            `requested ${row.requestedAt}`,
          ]
            .filter((part): part is string => part !== null)
            .join(' · ')}
        </span>
      </div>
      {canManage && (
        <div className="flex shrink-0 items-center gap-8">
          <Button
            appearance="saas"
            tone="primary"
            size="sm"
            leadingIcon={<Icon icon={Check} size={14} aria-hidden />}
            // Same predicate as the queue: never open a charge we cannot price.
            disabled={row.amountValue === null || rejectingId === row.id}
            onClick={() => onApprove(row)}
            testId={`needs-attention-approve-${row.id}`}
          >
            Approve
          </Button>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            leadingIcon={<Icon icon={X} size={14} aria-hidden />}
            disabled={rejectingId === row.id}
            onClick={() => onDecline(row)}
            testId={`needs-attention-decline-${row.id}`}
          >
            Decline
          </Button>
        </div>
      )}
    </li>
  );
}

/** A non-approval signal: an icon, a sentence, and at most one action. */
function SignalRow({
  icon,
  title,
  detail,
  action,
  testId,
}: {
  icon: typeof Wallet;
  title: string;
  detail: string;
  action?: React.ReactNode;
  testId: string;
}) {
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-12 rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12"
      {...testIdProp(testId)}
    >
      <div className="flex min-w-0 items-start gap-10">
        <span className="mt-2 shrink-0 text-icon">
          <Icon icon={icon} size={16} aria-hidden />
        </span>
        <div className="flex min-w-0 flex-col gap-2">
          <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            {title}
          </span>
          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
            {detail}
          </span>
        </div>
      </div>
      {action != null && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function NeedsAttention({
  rows,
  pendingCount,
  approvalTotal,
  canManage,
  isOwnerConfirmed,
  canApproveAll,
  rejectingId,
  onApprove,
  onDecline,
  onApproveAll,
}: {
  /** Every approval row; the summary picks the pending ones itself. */
  rows: MatchApprovalRow[];
  pendingCount: number;
  approvalTotal: number | null;
  canManage: boolean;
  /** Owner CONFIRMED by a resolved role read — gates the Owner-only join-request READ. */
  isOwnerConfirmed: boolean;
  canApproveAll: boolean;
  rejectingId: string | null;
  onApprove: (row: MatchApprovalRow) => void;
  onDecline: (row: MatchApprovalRow) => void;
  onApproveAll: () => void;
}) {
  const paths = useEmployerModulePaths();
  // Join requests: flag + Owner gated. Gated on the CONFIRMED Owner role, deliberately not on the
  // fail-open `canManage` — that is true while the role read is still in flight, so a Member's browser
  // would fire a request the backend 403s. `enabled: false` means the query never runs at all, so a
  // Member and a flag-off environment see nothing rather than a zero they are not entitled to read.
  const joinRequests = usePendingJoinRequests(isCompanyJoinOrCreateEnabled() && isOwnerConfirmed);
  // The host's inbox count. The hook is the host's (a constant per host), so it is the same hook on
  // every render; a host with no inbox reads nothing.
  const useAiRequestsWaiting = paths.aiRequests?.useWaiting ?? useNoAiRequestsWaiting;
  const aiRequests = useAiRequestsWaiting();

  const wallet = useQuery(companiesGetCompanyWalletOptions());
  // Spendable = available_balance (balance − holds) when the wire carries it, else balance — the same
  // basis the approve dialog's wallet option and the Settings balance card use.
  const spendableRaw =
    (typeof wallet.data?.available_balance === 'string' &&
    wallet.data.available_balance.trim().length > 0
      ? wallet.data.available_balance
      : null) ?? (typeof wallet.data?.balance === 'string' ? wallet.data.balance : null);
  const walletLow = wallet.isSuccess && isWalletLow(parseCentsAmount(spendableRaw));
  const walletDisplay = spendableRaw !== null ? formatMoneyWithSymbol(spendableRaw) : null;

  const pendingRows = rows.filter((r) => r.status === 'Pending');
  const showApprovals = pendingRows.length > 0;
  const showJoinRequests = joinRequests.pendingCount !== null && joinRequests.pendingCount > 0;
  const showWallet = walletLow && walletDisplay !== null;
  const showAiRequests = paths.aiRequests !== null && aiRequests !== null && aiRequests.count > 0;

  // The whole block is data-driven: nothing to attend to ⇒ nothing rendered.
  if (!showApprovals && !showJoinRequests && !showWallet && !showAiRequests) return null;

  const shown = pendingRows.slice(0, NEEDS_ATTENTION_ROW_LIMIT);
  const hidden = pendingRows.length - shown.length;

  return (
    <section
      className="flex flex-col gap-12 rounded-xl border border-border-subtle bg-surface p-20 shadow-xs"
      aria-labelledby="needs-attention-heading"
      {...testIdProp('needs-attention')}
    >
      <div className="flex flex-wrap items-center justify-between gap-12">
        <div className="flex items-center gap-8">
          <span className="text-warning-600">
            <Icon icon={AlertCircle} size={18} aria-hidden />
          </span>
          <h2
            id="needs-attention-heading"
            className="font-sans text-s15 font-w600 tracking-givmo text-text-strong"
          >
            Needs attention
          </h2>
        </div>
        {showApprovals && (
          <Link
            to={paths.approvals}
            className="inline-flex items-center gap-4 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
            {...testIdProp('needs-attention-see-all')}
          >
            See all approvals
            <Icon icon={ArrowRight} size={14} aria-hidden />
          </Link>
        )}
      </div>

      {showApprovals && (
        <div className="flex flex-col gap-10" {...testIdProp('needs-attention-approvals')}>
          <div className="flex flex-wrap items-center justify-between gap-12">
            <div className="flex items-center gap-8">
              <Badge tone="warning">{pendingCount} waiting</Badge>
              {/* Approve all's total: the rows' figures, which claim the budgets in request order,
                  so it promises nothing the budgets cannot cover. Absent with nothing to approve. */}
              {approvalTotal !== null && (
                <span
                  className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary tabular-nums"
                  {...testIdProp('needs-attention-total')}
                >
                  {formatApprovalUsd(approvalTotal)} to approve
                </span>
              )}
            </div>
            {canManage && (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={!canApproveAll}
                onClick={onApproveAll}
                testId="needs-attention-approve-all"
              >
                {canApproveAll && approvalTotal !== null
                  ? `Approve all (${formatApprovalUsd(approvalTotal)})`
                  : 'Approve all'}
              </Button>
            )}
          </div>

          <ul className="flex flex-col">
            {shown.map((row) => (
              <SummaryRow
                key={row.id}
                row={row}
                canManage={canManage}
                rejectingId={rejectingId}
                onApprove={onApprove}
                onDecline={onDecline}
              />
            ))}
          </ul>

          {hidden > 0 && (
            <p
              className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary tabular-nums"
              {...testIdProp('needs-attention-more')}
            >
              {`+ ${hidden} more ${hidden === 1 ? 'request' : 'requests'} in the queue`}
            </p>
          )}
        </div>
      )}

      {showJoinRequests && (
        <SignalRow
          testId="needs-attention-join-requests"
          icon={UserPlus}
          title={`${joinRequests.pendingCount}${joinRequests.atLeast ? '+' : ''} ${
            joinRequests.pendingCount === 1 && !joinRequests.atLeast ? 'person is' : 'people are'
          } waiting to join your admin team`}
          detail={paths.joinRequests.detail}
          action={
            <Link
              to={paths.joinRequests.to}
              className="inline-flex items-center gap-4 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
              {...testIdProp('needs-attention-join-requests-link')}
            >
              {paths.joinRequests.label}
              <Icon icon={ArrowRight} size={14} aria-hidden />
            </Link>
          }
        />
      )}

      {showAiRequests && paths.aiRequests !== null && aiRequests !== null && (
        <SignalRow
          testId="needs-attention-ai-requests"
          icon={BotMessageSquare}
          title={aiRequestsWaitingTitle(aiRequests.count, aiRequests.atLeast)}
          // DRAFT — the inbox's own line (its page description), in the plural.
          detail="They need a person’s approval before Givmo acts on them."
          action={
            <Link
              to={paths.aiRequests.to}
              className="inline-flex items-center gap-4 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
              {...testIdProp('needs-attention-ai-requests-link')}
            >
              {paths.aiRequests.label}
              <Icon icon={ArrowRight} size={14} aria-hidden />
            </Link>
          }
        />
      )}

      {showWallet && (
        <SignalRow
          testId="needs-attention-low-balance"
          icon={Wallet}
          title="Your company wallet is running low"
          // "we flag" — never "your threshold": no such setting exists (see LOW_WALLET_BALANCE_DOLLARS).
          detail={`${walletDisplay} available. We flag balances under $${LOW_WALLET_BALANCE_DOLLARS}, because a wallet-funded approval fails once it runs out.`}
          action={
            canManage ? (
              <Link
                to={paths.paymentMethods.to}
                search={paths.paymentMethods.search}
                className="inline-flex items-center gap-4 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
                {...testIdProp('needs-attention-low-balance-link')}
              >
                {paths.paymentMethods.label}
                <Icon icon={ArrowRight} size={14} aria-hidden />
              </Link>
            ) : undefined
          }
        />
      )}
    </section>
  );
}
